import { useRef } from 'react';
import { useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import Tooltip from '@/components/ui/tooltip';
import type { AnimatedIconHandle } from '@/components/icons/animated-icon';
import { Moon } from '@/components/icons/moon';
import { Sun } from '@/components/icons/sun';
import { useSystemTheme } from '../../../../hooks/useSystemTheme';
import { updateCurrentUserInfo } from '../../../../services/auth';
import { hapticsImpactLight } from '../../../../utils/haptics';
import type { RootState } from '../../../../store/store';
import type { ThemeMode } from '../../../../types/User';

/**
 * The theme the user actually sees: an explicit choice wins, otherwise the OS
 * preference. Toggling from "system" pins the opposite of what the OS shows
 * right now, so the click always visibly flips the page.
 */
export const resolveNextTheme = (
  theme: ThemeMode | undefined,
  systemTheme: ThemeMode
): 'light' | 'dark' => {
  const effective = theme === 'light' || theme === 'dark' ? theme : systemTheme;

  return effective === 'dark' ? 'light' : 'dark';
};

const ThemeToggleButton = () => {
  const theme = useSelector((state: RootState) => state.user.profile?.theme);
  const systemTheme = useSystemTheme();
  const nextTheme = resolveNextTheme(theme, systemTheme);
  const label = `Switch to ${nextTheme} theme`;
  // The icon animates while the whole button is hovered, not just the glyph,
  // so the trigger lives on the Button and drives the icon through its handle.
  const iconRef = useRef<AnimatedIconHandle>(null);
  const Icon = nextTheme === 'light' ? Sun : Moon;

  return (
    <Tooltip title={label}>
      <Button
        variant="ghost"
        size="icon-lg"
        aria-label={label}
        onMouseEnter={() => iconRef.current?.play()}
        onMouseLeave={() => iconRef.current?.stop()}
        onClick={() => {
          hapticsImpactLight();
          void updateCurrentUserInfo({ theme: nextTheme });
        }}
      >
        <Icon ref={iconRef} trigger="none" aria-hidden="true" />
      </Button>
    </Tooltip>
  );
};

export default ThemeToggleButton;
